// Global Instructions Rule Applied!

/**
 * Minimal ZIP writer, STORE (no compression) only.
 *
 * Exists so the Reports screen can hand back one archive when a report is
 * generated for several funds, without adding a compression library to the
 * bundle. STORE rather than DEFLATE is not a shortcut: the members are PDFs,
 * which are already Flate-compressed internally, so deflating them again buys
 * a percent or two for a whole codec.
 *
 * Deliberately not general-purpose — no zip64, no encryption, no directory
 * entries, no streaming. Everything is held in memory, which is right for a
 * few dozen documents and wrong for anything larger. If a caller ever needs
 * more than that, reach for a real library rather than growing this.
 */

/** One file to place in the archive. */
export interface ZipEntry {
  /** Name as it should appear inside the archive, e.g. 'Report - Fund.pdf'. */
  name: string
  /** Backed by a plain ArrayBuffer so the bytes can go straight into a Blob. */
  bytes: Uint8Array<ArrayBuffer>
}

// Standard CRC-32 (IEEE 802.3), the checksum every zip entry carries. Built
// once on first use rather than at module load — the screen that needs it is
// not on the app's startup path.
let crcTable: Uint32Array | null = null

function crc32Table(): Uint32Array {
  if (crcTable) return crcTable
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i += 1) {
    let c = i
    for (let bit = 0; bit < 8; bit += 1) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    }
    table[i] = c >>> 0
  }
  crcTable = table
  return table
}

function crc32(bytes: Uint8Array): number {
  const table = crc32Table()
  let c = 0xffffffff
  for (let i = 0; i < bytes.length; i += 1) {
    c = table[(c ^ bytes[i]) & 0xff] ^ (c >>> 8)
  }
  return (c ^ 0xffffffff) >>> 0
}

/**
 * Entry names are stored as UTF-8 with the language-encoding flag set, so
 * non-ASCII fund names survive the round trip into Finder and Explorer.
 */
function encodeName(name: string): Uint8Array {
  return new TextEncoder().encode(name)
}

/** Little-endian writer over a fixed buffer — every zip field is LE. */
class ByteWriter {
  private readonly view: DataView
  private readonly bytes: Uint8Array<ArrayBuffer>
  private offset = 0

  constructor(size: number) {
    this.bytes = new Uint8Array(size)
    this.view = new DataView(this.bytes.buffer)
  }

  u16(value: number): void {
    this.view.setUint16(this.offset, value, true)
    this.offset += 2
  }

  u32(value: number): void {
    this.view.setUint32(this.offset, value >>> 0, true)
    this.offset += 4
  }

  raw(value: Uint8Array): void {
    this.bytes.set(value, this.offset)
    this.offset += value.length
  }

  get position(): number {
    return this.offset
  }

  result(): Uint8Array<ArrayBuffer> {
    return this.bytes
  }
}

const LOCAL_HEADER_SIGNATURE = 0x04034b50
const CENTRAL_HEADER_SIGNATURE = 0x02014b50
const END_OF_CENTRAL_DIR_SIGNATURE = 0x06054b50
const LOCAL_HEADER_BYTES = 30
const CENTRAL_HEADER_BYTES = 46
const END_OF_CENTRAL_DIR_BYTES = 22
// 2.0 — the floor for STORE with UTF-8 names.
const VERSION_NEEDED = 20
// Bit 11: names and comments are UTF-8.
const FLAG_UTF8 = 0x0800
const METHOD_STORE = 0

/**
 * DOS date/time, the only timestamp a base zip record carries. Seconds hold
 * two-second resolution and the year is an offset from 1980, which is why an
 * archive's timestamps are never quite the ones you handed it.
 */
function dosDateTime(when: Date): { date: number; time: number } {
  const time =
    (when.getHours() << 11) | (when.getMinutes() << 5) | (Math.floor(when.getSeconds() / 2) & 0x1f)
  const date =
    ((Math.max(when.getFullYear() - 1980, 0) & 0x7f) << 9) |
    ((when.getMonth() + 1) << 5) |
    when.getDate()
  return { date, time }
}

/**
 * Disambiguate repeated names as 'Report (2).pdf', 'Report (3).pdf'.
 *
 * The format permits duplicate names, but extracting one overwrites the other,
 * so a colliding member would simply vanish on the way to disk. Two funds with
 * the same name is all it takes, and nothing enforces uniqueness there.
 */
function uniqueNames(entries: ZipEntry[]): ZipEntry[] {
  const seen = new Map<string, number>()
  return entries.map((entry) => {
    const count = seen.get(entry.name) ?? 0
    seen.set(entry.name, count + 1)
    if (count === 0) return entry
    const dot = entry.name.lastIndexOf('.')
    const stem = dot > 0 ? entry.name.slice(0, dot) : entry.name
    const extension = dot > 0 ? entry.name.slice(dot) : ''
    return { ...entry, name: `${stem} (${count + 1})${extension}` }
  })
}

/**
 * Build a zip archive from the given entries and return it as a Blob.
 *
 * `when` is injectable so a test can assert on exact bytes; it defaults to now.
 */
export function createZip(entries: ZipEntry[], when: Date = new Date()): Blob {
  const { date, time } = dosDateTime(when)

  const prepared = uniqueNames(entries).map((entry) => ({
    nameBytes: encodeName(entry.name),
    bytes: entry.bytes,
    crc: crc32(entry.bytes)
  }))

  const totalSize = prepared.reduce(
    (sum, e) =>
      sum + LOCAL_HEADER_BYTES + e.nameBytes.length + e.bytes.length +
      CENTRAL_HEADER_BYTES + e.nameBytes.length,
    END_OF_CENTRAL_DIR_BYTES
  )

  const writer = new ByteWriter(totalSize)
  const localOffsets: number[] = []

  // Local file header + payload, one per entry.
  for (const entry of prepared) {
    localOffsets.push(writer.position)
    writer.u32(LOCAL_HEADER_SIGNATURE)
    writer.u16(VERSION_NEEDED)
    writer.u16(FLAG_UTF8)
    writer.u16(METHOD_STORE)
    writer.u16(time)
    writer.u16(date)
    writer.u32(entry.crc)
    // Compressed and uncompressed sizes are equal under STORE.
    writer.u32(entry.bytes.length)
    writer.u32(entry.bytes.length)
    writer.u16(entry.nameBytes.length)
    writer.u16(0) // no extra field
    writer.raw(entry.nameBytes)
    writer.raw(entry.bytes)
  }

  // Central directory: one record per entry, pointing back at its local header.
  const centralStart = writer.position
  prepared.forEach((entry, index) => {
    writer.u32(CENTRAL_HEADER_SIGNATURE)
    writer.u16(VERSION_NEEDED) // version made by
    writer.u16(VERSION_NEEDED) // version needed to extract
    writer.u16(FLAG_UTF8)
    writer.u16(METHOD_STORE)
    writer.u16(time)
    writer.u16(date)
    writer.u32(entry.crc)
    writer.u32(entry.bytes.length)
    writer.u32(entry.bytes.length)
    writer.u16(entry.nameBytes.length)
    writer.u16(0) // extra field length
    writer.u16(0) // comment length
    writer.u16(0) // disk number start
    writer.u16(0) // internal attributes
    writer.u32(0) // external attributes
    writer.u32(localOffsets[index])
    writer.raw(entry.nameBytes)
  })

  const centralSize = writer.position - centralStart
  writer.u32(END_OF_CENTRAL_DIR_SIGNATURE)
  writer.u16(0) // this disk
  writer.u16(0) // disk with central directory
  writer.u16(prepared.length)
  writer.u16(prepared.length)
  writer.u32(centralSize)
  writer.u32(centralStart)
  writer.u16(0) // archive comment length

  return new Blob([writer.result()], { type: 'application/zip' })
}

// Shortest derived archive stem worth using; below this the shared text is
// more likely coincidence than a common title.
const MIN_DERIVED_STEM = 3

/** Longest common prefix of the given strings ('' when they share nothing). */
function commonPrefix(values: string[]): string {
  if (values.length === 0) return ''
  let prefix = values[0]
  for (const value of values.slice(1)) {
    let i = 0
    while (i < prefix.length && i < value.length && prefix[i] === value[i]) i += 1
    prefix = prefix.slice(0, i)
    if (!prefix) break
  }
  return prefix
}

function commonSuffix(values: string[]): string {
  const reverse = (s: string) => s.split('').reverse().join('')
  return reverse(commonPrefix(values.map(reverse)))
}

/**
 * Name an archive after what its members have in common.
 *
 * Report filenames look like `Quarterly_Fund_Report_-_Origin_21_-_2026_Q1.pdf`,
 * so the part that varies is the middle and the shared head and tail are
 * exactly the report and the period — dropping the middle yields
 * `Quarterly_Fund_Report_-_2026_Q1.zip`.
 *
 * Derived rather than composed from cfg's filename_pattern, because that
 * pattern's tokens are the report's own and `{fund_name}` is precisely the
 * piece being dropped; derived rather than hardcoded so a second report gets
 * sensible archive names with no new code.
 */
/** A name no other member of the archive carries: two entries with one name
 *  would unpack as one file. `used` collects the names taken, lower-cased. */
export function uniqueArchiveName(name: string, used: Set<string>): string {
  let candidate = name
  for (let n = 2; used.has(candidate.toLowerCase()); n += 1) {
    candidate = name.replace(/(\.[A-Za-z0-9]+)?$/, (ext) => ` (${n})${ext}`)
  }
  used.add(candidate.toLowerCase())
  return candidate
}

export function archiveNameFor(filenames: string[], fallbackStem: string): string {
  const stems = filenames.map((name) => name.replace(/\.[A-Za-z0-9]+$/, ''))
  if (stems.length === 0) return `${fallbackStem}.zip`
  if (stems.length === 1) return `${stems[0]}.zip`

  // Snap both ends back to a separator. The raw common prefix runs to the
  // first differing CHARACTER, which bleeds into the varying part whenever the
  // members happen to share a letter there — 'ACE Labs' and 'Acecap' share an
  // 'A', which is how the archive for four A-named funds came out as
  // `Quarterly_Fund_Report_-_A2026_Q1`. Only a separator is a real boundary.
  const prefix = commonPrefix(stems).replace(/[^\s\-_]*$/, '')
  const suffix = commonSuffix(stems).replace(/^[^\s\-_]*/, '')

  // Identical names, or heads and tails that overlap: there is no meaningful
  // middle to drop, so keep the whole name.
  if (prefix.length + suffix.length >= stems[0].length) return `${stems[0]}.zip`

  // Join on the SUFFIX's separator rather than the prefix's: whatever the
  // backend used (' - ' or '_-_') then survives intact, where keeping the
  // prefix's would flatten `Origin_21_-_2026_Q1` to `Origin_2026_Q1` when the
  // selected funds share a name stem.
  const head = prefix.replace(/[\s\-_]+$/, '')
  const stem = `${head}${suffix}`.replace(/[\s\-_]+$/, '')

  // Unrelated names can still share an incidental letter or two ('alpha' and
  // 'beta' share a trailing 'a'), which would name the archive 'a.zip'. Only
  // trust a stem substantial enough to be a real shared title.
  return `${stem.length >= MIN_DERIVED_STEM ? stem : fallbackStem}.zip`
}
